import React, { useContext, useState, useCallback, cloneElement } from "react";
import {
  TableCell,
  TableRow,
  Typography,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
  colors,
  Divider,
  Hidden,
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { AlertTriangle, Check } from "react-feather";

import {
  ChevronDown as ChevronDownIcon,
  ChevronUp as ChevronUpIcon,
  RefreshCcw as RefreshIcon,
  Airplay as AirplayIcon,
} from "react-feather";
import PlantPage from "../../pages/PlantPage";
import EklokPage from "../../pages/EklokPage";
import Modal from "../../components/Modal";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { getConnectedStatusString, getFormattedDate } from "../../utils/utils";
import { useHistory } from "react-router";
import { mobileFrameHiddenProps } from "../../constants";
import { UseMe } from "../../api/users/Me.api";
import { AuthContext } from "../../context/auth/AuthContext";
import { useMobileFrame } from "../../containers/mobile-frame/MobileFrameHomeContainer";
import { useDispatch } from "react-redux";
import { initConnectionLineStatus } from "../../store/eklok-line-status.store";

const useStyles = makeStyles((theme) => ({
  buttons: {
    "& > *": {
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1),
      [theme.breakpoints.down("xs")]: {
        marginLeft: 0,
      },
    },
  },
  connected: {
    color: colors.green[500],
    fontWeight: "bold",
  },
  not_connected: {
    color: colors.red[900],
    fontWeight: "bold",
  },
  card: {
    margin: "15px 0",
  },
}));

const Row = ({
  item,
  headCells,
  onRefreshAction = () => {},
  onConnectDevice = () => {},
}) => {
  const history = useHistory();
  const classes = useStyles();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down("xs"));
  const [open, setOpen] = useState(false);
  const { token } = useContext(AuthContext);
  const { content: user } = UseMe(token);
  const dispatch = useDispatch();

  const setEklokStatusLineConnection = useCallback(
    (eklokId) => {
      dispatch(
        initConnectionLineStatus({
          token: token,
          body: { eklok: eklokId, line_status: true, process: null },
        }),
      );
    },
    [dispatch, token],
  );

  const goToMobileFrame = useMobileFrame();
  const getInfo = useCallback(
    (id) => {
      switch (id) {
        case "connected":
          return (
            <Typography
              className={
                item?.connected ? classes.connected : classes.not_connected
              }
            >
              {getConnectedStatusString(item[id])}
            </Typography>
          );
        case "last_mqtt_update":
          return <Typography>{getFormattedDate(item[id])}</Typography>;
        case "error":
          return (
            <Typography>
              {item.alarm_list?.find(
                (alarm) => alarm.ALARM_STATUS === "ACTIVE",
              ) ? (
                <AlertTriangle color="red" />
              ) : (
                <Check color="green" />
              )}{" "}
            </Typography>
          );
        case "name":
        case "hw_id":
        case "serial_id":
        case "hmi_version":
        case "software_version":
          return <Typography>{item[id]}</Typography>;
        case "connect":
          return (
            <div className={classes.buttons}>
              <Tooltip title="Refresh">
                <IconButton
                  size="small"
                  onClick={() => onRefreshAction([item])}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              {item.connected ? (
                <Tooltip title="Connect">
                  <IconButton
                    size="small"
                    onClick={() => {
                      goToMobileFrame(history, item.id);
                      setEklokStatusLineConnection(item.id);
                    }}
                  >
                    <AirplayIcon />
                  </IconButton>
                </Tooltip>
              ) : null}
              <Tooltip title="Detail">
                <IconButton size="small" onClick={() => setOpen(!open)}>
                  {open ? <ChevronUpIcon /> : <ChevronDownIcon />}
                </IconButton>
              </Tooltip>
            </div>
          );
        default:
          return null;
      }
    },
    [
      item,
      open,
      classes.buttons,
      classes.connected,
      classes.not_connected,
      goToMobileFrame,
      history,
      onRefreshAction,
      setEklokStatusLineConnection,
    ],
  );

  const getRenderPlantPage = () => {
    if (user && user.groups) {
      switch (user.groups[0].name) {
        case "Admin":
          return <PlantPage item={item.plant} />;

        case "Installer":
          return <PlantPage item={item.plant} />;

        case "Customer":
          return null;

        default:
          return null;
      }
    }
  };

  return (
    <>
      <Hidden xsDown>
        <TableRow key={item.id}>
          <TableCell>{item.name}</TableCell>
          <TableCell>{item.hw_id}</TableCell>
          <TableCell>{item.serial_id}</TableCell>
          <TableCell>{item.software_version}</TableCell>
          <TableCell>{getFormattedDate(item.last_mqtt_update)}</TableCell>
          <TableCell
            className={
              item.connected ? classes.connected : classes.not_connected
            }
          >
            {getConnectedStatusString(item.connected)}
          </TableCell>
          <TableCell>
            {item.alarm_list &&
            item.alarm_list.find((alarm) => alarm.ALARM_STATUS === "ACTIVE") ? (
              <AlertTriangle color="red" />
            ) : (
              <Check color="green" />
            )}
          </TableCell>

          <TableCell>
            <div className={classes.buttons}>
              <Tooltip title="Refresh">
                <IconButton
                  size="small"
                  onClick={() => onRefreshAction([item])}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              {item.connected ? (
                <Tooltip title="Connect">
                  <IconButton
                    size="small"
                    onClick={() => onConnectDevice(item)}
                  >
                    <AirplayIcon />
                  </IconButton>
                </Tooltip>
              ) : null}
              <Tooltip title="Detail">
                <IconButton size="small" onClick={() => setOpen(!open)}>
                  {open ? <ChevronUpIcon /> : <ChevronDownIcon />}
                </IconButton>
              </Tooltip>
            </div>
          </TableCell>
        </TableRow>
      </Hidden>

      {cloneElement(
        <Hidden>
          <Card className={classes.card}>
            <CardContent>
              {headCells?.map((headCell) => (
                <Grid container key={headCell.id}>
                  <Grid item xs={5}>
                    <Typography variant="h5">{headCell.label}</Typography>
                  </Grid>
                  <Grid item xs={7}>
                    {getInfo(headCell.id)}
                  </Grid>
                </Grid>
              ))}
            </CardContent>
          </Card>
        </Hidden>,
        mobileFrameHiddenProps,
      )}

      {open ? (
        <Modal
          fullScreen={matches}
          title={item.name}
          onClose={() => setOpen(false)}
          open={open}
          fullWidth={true}
          maxWidth="lg"
        >
          {getRenderPlantPage()}
          <Divider />
          <EklokPage item={item} />
        </Modal>
      ) : null}
    </>
  );
};

export default Row;
